"use client";

import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from "react";
import { HttpTypes } from "@medusajs/types";
import { retrieveCustomer } from "@/modules/customer/actions/customer";

interface CustomerContextType {
  customer: HttpTypes.StoreCustomer | null;
  setCustomer: (customer: HttpTypes.StoreCustomer | null) => void;
  refreshCustomer: () => Promise<HttpTypes.StoreCustomer | null>;
  isLoading: boolean;
}

const CustomerContext = createContext<CustomerContextType | undefined>(
  undefined
);

export function CustomerProvider({ children }: { children: ReactNode }) {
  const [customer, setCustomer] = useState<HttpTypes.StoreCustomer | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);

  const refreshCustomer = useCallback(async () => {
    setIsLoading(true);
    try {
      const customerData = await retrieveCustomer();
      setCustomer(customerData);
      return customerData;
    } catch (error) {
      console.error("Error fetching customer:", error);
      setCustomer(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshCustomer();
  }, [refreshCustomer]);

  return (
    <CustomerContext.Provider
      value={{ customer, setCustomer, refreshCustomer, isLoading }}
    >
      {children}
    </CustomerContext.Provider>
  );
}

export function useCustomer() {
  const context = useContext(CustomerContext);
  if (context === undefined) {
    throw new Error("useCustomer must be used within a CustomerProvider");
  }
  return context;
}
